import React, { Fragment } from "react";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import {
  Typography,
  Chip,
  IconButton,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { formatInTimeZone } from "date-fns-tz";
import Labels from "../labels/Labels";
import ForwardToInboxIcon from "@mui/icons-material/ForwardToInbox";
import RuleIcon from "@mui/icons-material/Rule";

export default function SendMoneyTransferList(props) {
  const {
    onResendTransfer,
    isResending,
    resendingId,
    transferType,
    page,
    pageSize,
    onPageChange,
    onPageSizeChange,
    enablePagination,
    data,
    isLoading,
    isError,
    onGetBeneficiaryEvents,
  } = props;

  const processingHeader = Labels.getLabel(
    "sendMoneyTransfers.processingDate",
    "Processing Date"
  );

  // Status configuration for consistent color and label mapping
  const STATUS_CONFIG = {
    PENDING: { color: "warning", label: "PENDING" },
    INPROCESS: { color: "warning", label: "PROCESSING" },
    SENT: { color: "success", label: "SENT" },
    COMPLETED: { color: "success", label: "COMPLETED" },
    FAILED: { color: "error", label: "FAILED" },
    CHARGES_RETURNED: { color: "error", label: "CHARGES_RETURNED" },
    QOLO_RECIPIENT: { color: "warning", label: "WAITING_FOR_RECIPIENT" },
  };

  // Helper function to get beneficiary name - reused in valueGetter and renderCell
  const getBeneficiaryName = (beneficiary) => {
    if (!beneficiary) return "-";

    const firstName = beneficiary.firstName ?? "";
    const lastName = beneficiary.lastName ?? "";

    return beneficiary.isOwn
      ? beneficiary.alias ||
          Labels.getLabel("sendMoneyTransfers.ownAccount", "Own Account")
      : (firstName + " " + lastName).trim() ||
          Labels.getLabel("sendMoneyTransfers.unknown", "Unknown Beneficiary");
  };

  // Helper function to get amount - reused in valueGetter and renderCell
  const getTransferAmount = (params) => {
    const displayAmount =
      transferType === "instant"
        ? params.row.referenceAmount
        : params.row.amount;
    return `$${displayAmount?.toFixed(2) || "0.00"}`;
  };

  // Helper function to get status config - reused in valueGetter and renderCell
  const getStatusConfig = (params) => {
    const statusUpper = params.row.status?.toUpperCase();
    return (
      STATUS_CONFIG[statusUpper] || {
        color: "default",
        label: statusUpper || "--",
      }
    );
  };

  // Helper function to format date - reused in valueGetter and renderCell
  const formatTransferDate = (params) => {
    const dateStr =
      transferType === "instant"
        ? params.row.creationDate
        : params.row.processingDate;

    if (!dateStr) return "-";

    try {
      const cleanedDate = dateStr.replace(/\[.*?\]$/, "");

      if (transferType === "scheduled") {
        const paymentFrequency = params.row.paymentFrequency;

        if (paymentFrequency === "Weekly") {
          const dayName = formatInTimeZone(
            cleanedDate,
            "UTC",
            "EEEE"
          ).toUpperCase();
          return `ON ${dayName}S`;
        } else if (paymentFrequency === "Monthly") {
          const dayOfMonth = formatInTimeZone(cleanedDate, "UTC", "d");
          return `ON DAY ${dayOfMonth}`;
        } else if (paymentFrequency === "OneTime") {
          return formatInTimeZone(cleanedDate, "UTC", "MM/dd/yyyy HH:mm:ss");
        } else {
          return formatInTimeZone(cleanedDate, "UTC", "MM/dd/yyyy HH:mm:ss");
        }
      } else {
        return formatInTimeZone(cleanedDate, "UTC", "MM/dd/yyyy HH:mm:ss");
      }
    } catch (e) {
      return dateStr;
    }
  };

  const columns = [
    {
      field: "actions",
      headerName: Labels.getLabel("sendMoneyTransfers.actions", "Actions"),
      width: 150,
      sortable: false,
      hide: false,
      renderCell: (params) => {
        const isQoloRecipient = "QOLO_RECIPIENT" === params.row.status;

        if (isQoloRecipient) {
          const idMoneyMovement = params.row.id;

          // Check if THIS specific row is being processed
          const isThisRowProcessing =
            isResending && resendingId === idMoneyMovement;

          return (
            <Tooltip
              title={Labels.getLabel(
                "sendMoneyTransfers.resend",
                "Resend LINK"
              )}
            >
              <IconButton
                size="small"
                onClick={() => onResendTransfer(idMoneyMovement)}
                disabled={isResending || !idMoneyMovement}
                color="primary"
              >
                {isThisRowProcessing ? (
                  <CircularProgress color="secondary" size="1rem" />
                ) : (
                  <>
                    <ForwardToInboxIcon fontSize="small" />
                  </>
                )}
              </IconButton>
            </Tooltip>
          );
        }

        return <></>;
      },
    },
    {
      field: "transferId",
      headerName: Labels.getLabel(
        "sendMoneyTransfers.transferId",
        "Transfer ID"
      ),
      width: 100,
      hide: true,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
            {params.row.transferId}
          </Typography>
        );
      },
    },
    {
      field: "confirmationNumber",
      headerName: Labels.getLabel(
        "sendMoneyTransfers.confirmationNumber",
        "Confirmation Number"
      ),
      width: 150,
      hide: true,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
            {params.row.confirmationNumber}
          </Typography>
        );
      },
    },
    {
      field: "beneficiary",
      headerName: Labels.getLabel(
        "sendMoneyTransfers.beneficiary",
        "Beneficiary"
      ),
      width: 220,
      valueGetter: (params) => {
        const beneficiary = params.row.moneyMovementBeneficiary;
        const name = getBeneficiaryName(beneficiary);
        const contact = beneficiary?.idCommunication || "";
        const mask = beneficiary?.mask || "";
        return `${name} ${contact} ${mask}`.trim();
      },
      renderCell: (params) => {
        const beneficiary = params.row.moneyMovementBeneficiary;

        if (!beneficiary) {
          return <Typography sx={{ fontSize: 12, mb: 0 }}>-</Typography>;
        }

        const beneficiaryName = getBeneficiaryName(beneficiary);
        const contact = beneficiary.idCommunication;
        const mask = beneficiary.mask;

        return (
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              width: "100%",
              minHeight: "40px",
            }}
          >
            <div
              style={{
                flex: 1,
                minWidth: 0, // Allow text to truncate if needed
                paddingRight: "8px",
              }}
            >
              <Typography sx={{ fontSize: 12, fontWeight: 500, mb: 0 }}>
                {beneficiaryName}
              </Typography>
              {contact && (
                <Typography
                  sx={{ fontSize: 11, color: "text.secondary", mb: 0 }}
                >
                  {contact}
                </Typography>
              )}
              {mask && (
                <Typography
                  sx={{ fontSize: 11, color: "text.secondary", mb: 0 }}
                >
                  {mask}
                </Typography>
              )}
            </div>
            <div
              style={{
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                height: "40px",
              }}
            >
              {onGetBeneficiaryEvents && (
                <Tooltip
                  title={`${
                    beneficiary.isOwn ? "Own Account" : "External"
                  } - Card Validation Events`}
                >
                  <IconButton
                    size="small"
                    onClick={() => onGetBeneficiaryEvents(beneficiary)}
                    color="primary"
                  >
                    <RuleIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </div>
          </div>
        );
      },
    },
    {
      field: "isOwn",
      headerName: Labels.getLabel("sendMoneyTransfers.isOwn", "Own Account"),
      width: 120,
      valueGetter: (params) =>
        params.row.moneyMovementBeneficiary?.isOwn === true ? "Yes" : "No",
      renderCell: (params) => {
        const isOwn = params.row.moneyMovementBeneficiary?.isOwn === true;
        return (
          <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
            {isOwn ? "Yes" : "No"}
          </Typography>
        );
      },
    },
    {
      field: "communicationType",
      headerName: Labels.getLabel(
        "sendMoneyTransfers.communicationType",
        "Communication Type"
      ),
      width: 160,
      renderHeader: () => (
        <div style={{ whiteSpace: "normal", lineHeight: "1.2" }}>
          {Labels.getLabel(
            "sendMoneyTransfers.communicationType",
            "Communication Type"
          )}
        </div>
      ),
      valueGetter: (params) =>
        params.row.moneyMovementBeneficiary?.typeCommunication || "-",
      renderCell: (params) => {
        const beneficiary = params.row.moneyMovementBeneficiary;
        return (
          <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
            {beneficiary?.typeCommunication || "-"}
          </Typography>
        );
      },
    },
    {
      field: "amount",
      headerName: Labels.getLabel("sendMoneyTransfers.amount", "Amount"),
      width: 100,
      valueGetter: getTransferAmount,
      renderCell: (params) => {
        const formattedAmount = getTransferAmount(params);
        return (
          <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
            {formattedAmount}
          </Typography>
        );
      },
    },
    {
      field: "status",
      headerName: Labels.getLabel("sendMoneyTransfers.status", "Status"),
      width: 180,
      valueGetter: (params) => {
        const config = getStatusConfig(params);
        return config.label;
      },
      renderCell: (params) => {
        const config = getStatusConfig(params);

        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Chip
              label={config.label}
              color={config.color}
              size="small"
              sx={{
                fontSize: "10px",
                fontWeight: "bold",
                height: "20px",
                minWidth: "120px",
              }}
            />
            {params.row.description && (
              <Typography
                sx={{
                  fontSize: 9,
                  fontWeight: "bold",
                  color: "text.secondary",
                  mb: 0,
                  mt: 0.5,
                  textAlign: "center",
                  lineHeight: 1.2,
                }}
              >
                {params.row.description}
              </Typography>
            )}
          </div>
        );
      },
    },
    {
      field: "paymentFrequency",
      headerName: Labels.getLabel(
        "sendMoneyTransfers.paymentFrequency",
        "Payment Frequency"
      ),
      width: 130,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
            {params.row.paymentFrequency || "-"}
          </Typography>
        );
      },
    },
    {
      field: "processingDate",
      headerName: processingHeader,
      width: 160,
      valueGetter: formatTransferDate,
      renderCell: (params) => {
        const formattedDate = formatTransferDate(params);
        return (
          <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
            {formattedDate}
          </Typography>
        );
      },
    },
    {
      field: "transferType",
      headerName: Labels.getLabel(
        "sendMoneyTransfers.transferType",
        "Transfer Type"
      ),
      width: 120,
      hide: true,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
            {params.row.transferType || "-"}
          </Typography>
        );
      },
    },
    {
      field: "willRepeatUntilCancel",
      headerName: Labels.getLabel("sendMoneyTransfers.recurring", "Recurring"),
      width: 100,
      hide: true,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
            {params.row.willRepeatUntilCancel ? "Yes" : "No"}
          </Typography>
        );
      },
    },
  ];

  // Transform the data to add unique IDs for DataGrid
  const rows =
    data?.data?.transfers?.map((transfer, index) => ({
      id: transfer.transferId || transfer.id || index,
      ...transfer,
    })) || [];

  // Calculate rowCount for pagination without backend total
  // If we got a full page, assume there might be more; otherwise, this is the last page
  const hasFullPage = rows.length === pageSize;
  const rowCount = hasFullPage
    ? (page + 2) * pageSize
    : page * pageSize + rows.length;

  return (
    <Fragment>
      {isLoading ? (
        <h4>{Labels.getLabel("sendMoneyTransfers.loading", "Loading...")}</h4>
      ) : isError ? (
        <h4>
          {Labels.getLabel(
            "sendMoneyTransfers.error",
            "An error occurred, please try again later..."
          )}
        </h4>
      ) : rows.length === 0 ? (
        <Typography sx={{ p: 2, textAlign: "center" }}>
          {Labels.getLabel(
            "sendMoneyTransfers.noData",
            "No transfers found for the selected criteria."
          )}
        </Typography>
      ) : (
        <div style={{ height: 400, width: "100%" }}>
          <DataGrid
            disableSelectionOnClick
            disableColumnSelector
            disableDensitySelector
            disableColumnFilter
            rows={rows}
            columns={columns}
            page={enablePagination ? page : 0}
            pageSize={enablePagination ? pageSize : rows.length}
            rowsPerPageOptions={enablePagination ? [10] : []}
            rowCount={enablePagination ? rowCount : rows.length}
            paginationMode={enablePagination ? "server" : "client"}
            onPageChange={enablePagination ? onPageChange : undefined}
            onPageSizeChange={enablePagination ? onPageSizeChange : undefined}
            hideFooterSelectedRowCount
            hideFooterRowCount
            components={{ Toolbar: GridToolbar }}
            componentsProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 500 },
              },
            }}
            sx={{
              "& .MuiButton-root": {
                display: "none",
              },
              // "& .MuiTablePagination-displayedRows": {
              //     display: "none",
              // },
            }}
          />
        </div>
      )}
    </Fragment>
  );
}
